/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { getSelectedStore } from '../../utils/utils';
import { StoreConfigsApiResponse } from '../../types/types';
import { getStoreConfigs } from '../../api/user';

export const parseReportAccessArray = (
  api: StoreConfigsApiResponse
): string[] => {
  const raw =
    api?.storeProfileResponse?.configDetails?.[0]?.configDetails?.find(
      (c) => c.paramKeyName === 'CSMReportAccess'
    )?.paramValue ?? '';

  if (!raw) return [];

  const cleaned = String(raw)
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .trim();

  const quoted = Array.from(cleaned.matchAll(/"([^"]+)"|'([^']+)'/g))
    .map((m) => (m[1] ?? m[2] ?? '').trim())
    .filter(Boolean);

  if (quoted.length) return quoted;

  return cleaned
    .replace(/[{}\[\]"']/g, '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

interface StoreState {
  loader: boolean;
  hasApiError: boolean;
  reportAccessRoles: string[];
}

interface StoreDispatch {
  fetchStoreDetailsByNumber: (storeNumber: string) => Promise<string[] | void>;
}

const StoreStateContext = createContext<StoreState>({} as StoreState);
const StoreDispatchContext = createContext<StoreDispatch>({} as StoreDispatch);

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<StoreState>({
    loader: false,
    hasApiError: false,
    reportAccessRoles: [],
  });

  const fetchStoreDetailsByNumber = useCallback(async (storeNumber: string) => {
    setState((prev) => ({ ...prev, loader: true, hasApiError: false }));
    try {
      const response = await getStoreConfigs({
        storeNumbers: [storeNumber],
        paramKeyNames: ['CSMReportAccess'],
      });
      const roles = parseReportAccessArray(response);
      setState({ loader: false, hasApiError: false, reportAccessRoles: roles });
      return roles;
    } catch {
      setState((prev) => ({ ...prev, loader: false, hasApiError: true }));
    }
  }, []);

  useEffect(() => {
    const selectedStore = getSelectedStore?.();
    if (selectedStore) fetchStoreDetailsByNumber(selectedStore);
  }, [fetchStoreDetailsByNumber]);

  return (
    <StoreStateContext.Provider value={state}>
      <StoreDispatchContext.Provider value={{ fetchStoreDetailsByNumber }}>
        {children}
      </StoreDispatchContext.Provider>
    </StoreStateContext.Provider>
  );
};

export const useStoreDetails = () => useContext(StoreStateContext);
export const useStoreActions = () => useContext(StoreDispatchContext);
